import { z } from 'zod'

/**
 * Сотрудник. `role` — подпись под именем («гид», «водитель»), а `accessRole` —
 * право: администратор ведёт команду и выходы, сотрудник — только свой график.
 * Две разные вещи, и путать их нельзя: должность прав не даёт.
 *
 * Адрес чата мессенджера наружу не отдаётся никогда — только признак привязки.
 */
export const EmployeeSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    login: z.string(),
    role: z.string().default(''),
    access_role: z.enum(['admin', 'employee']).default('employee'),
    active: z.boolean(),
    telegram_linked: z.boolean(),
    /**
     * Уведомления доходят. false — человек закрыл бота: продукт показывает это
     * явно, иначе сообщения исчезают молча (дефект донора stit-hub).
     */
    telegram_reachable: z.boolean(),
  })
  .transform((dto) => ({
    id: dto.id,
    name: dto.name,
    login: dto.login,
    role: dto.role,
    isAdmin: dto.access_role === 'admin',
    active: dto.active,
    telegramLinked: dto.telegram_linked,
    telegramReachable: dto.telegram_reachable,
  }))

export type Employee = z.infer<typeof EmployeeSchema>

export const EmployeeListSchema = z.array(EmployeeSchema)

export const DeactivateResultSchema = z
  .object({
    employee: EmployeeSchema,
    /** Размер дыры: что повисло на человеке в будущем. Ничего не переназначается. */
    future_task_count: z.number().int(),
    future_excursion_count: z.number().int(),
  })
  .transform((dto) => ({
    employee: dto.employee,
    futureTaskCount: dto.future_task_count,
    futureExcursionCount: dto.future_excursion_count,
  }))

export type DeactivateResult = z.infer<typeof DeactivateResultSchema>

export const TelegramLinkSchema = z
  .object({
    /** Готовая ссылка `https://t.me/<bot>?start=<state>` — её открывает человек. */
    url: z.string(),
    expires_at: z.iso.datetime(),
  })
  .transform((dto) => ({ url: dto.url, expiresAt: dto.expires_at }))

export type TelegramLink = z.infer<typeof TelegramLinkSchema>

export interface NewEmployeeInput {
  name: string
  login: string
  password: string
  role?: string
  /** Пусто — заводим сотрудником: права выдаются отдельным решением. */
  accessRole?: AccessRole
}

/** Право доступа. Должность (`role`) к нему отношения не имеет. */
export type AccessRole = 'admin' | 'employee'

/** Имя для колонки сетки: «Мария П.» — фамилия целиком не влезает. */
export function shortName(employee: Employee): string {
  const [first, second] = employee.name.trim().split(/\s+/)
  if (!first) return employee.name
  return second ? `${first} ${second[0]}.` : first
}
